import React from 'react';
import { Mail, Phone, MapPin, Linkedin, MessageCircle, Clock } from 'lucide-react';

const Contact = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] pt-32 pb-24 lg:pt-44 lg:pb-32 overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2069&q=80"
            alt="Modern office environment"
            className="w-full h-full object-cover opacity-15" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight tracking-tight">
              Take the Next Step
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">
                - Deliberately
              </span>
            </h1>
            <p className="text-xl lg:text-2xl max-w-3xl font-light mb-12 text-slate-300">
              Let's discuss organisational readiness and execution.
            </p>
            <button
              onClick={() => scrollToSection('ready-to-move')}
              className="inline-flex items-center px-8 py-4 bg-amber-500 text-slate-900 font-semibold rounded-md hover:bg-amber-400 transition-all duration-200 shadow-lg"
            >
              Book a 20-minute strategy conversation
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                Let's Connect
              </h2>
              <p className="text-lg text-slate-600 mb-12 leading-relaxed">
                If your organisation is approaching a critical moment, a focused conversation can help clarify what comes next.
              </p>

              <div className="space-y-8">
                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-semibold mb-2">Email</h3>
                    <a
                      href="mailto:consult@nexstephr.com"
                      className="text-slate-600 hover:text-amber-600 transition-colors text-lg">
                      consult@nexstephr.com
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-semibold mb-2">Phone</h3>
                    <a
                      href="tel:+918928440225"
                      className="text-slate-600 hover:text-amber-600 transition-colors text-lg">
                      +91-8928440225
                    </a>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-semibold mb-2">Location</h3>
                    <p className="text-lg text-slate-600">Mumbai | Pune | Hyderabad</p>
                  </div>
                </div>

                {/* LinkedIn */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Linkedin className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-semibold mb-2">LinkedIn</h3>
                    <a
                      href="https://www.linkedin.com/company/nexstephr/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-amber-600 transition-colors text-lg text-slate-600">
                      www.linkedin.com/company/nexstephr
                    </a>
                  </div>
                </div>

                {/* Office Hours */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-semibold mb-2">Office Hours</h3>
                    <p className="text-lg text-slate-600">Monday - Friday: 9:30 AM - 6:00 PM IST</p>
                  </div>
                </div>
              </div>
            </div>

            {/* What Happens Next */}
            <div className="lg:pl-8">
              <div className="p-10 bg-slate-50 border border-slate-200 rounded-2xl">
                <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-8 leading-tight">
                  What Happens Next
                </h2>
                
                <div className="space-y-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">1</span>
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-semibold mb-2">Initial Discussion</h3>
                      <p className="text-slate-600 leading-relaxed">
                        We begin with a focused conversation to understand your context, priorities, and the outcomes you are working toward.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">2</span>
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-semibold mb-2">Diagnostic Assessment</h3>
                      <p className="text-slate-600 leading-relaxed">
                        Where there is alignment, we conduct a targeted diagnostic to surface the underlying issues - separating root causes from symptoms.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">3</span>
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-semibold mb-2">Proposal & Engagement</h3>
                      <p className="text-slate-600 leading-relaxed">
                        We share a clear, structured proposal outlining the approach, expected outcomes, and investment required. No jargon - just clarity on what we will do and why it matters.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-slate-200">
                  <h3 className="text-slate-900 font-semibold mb-3">Our Partnership Approach</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    Our work is built on trust, collaboration, and shared commitment. We take a considered approach to every engagement and are transparent where we believe a different solution or partner may be better suited. Our intent is always to guide organisations toward the right outcome - even if that path does not involve us.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="ready-to-move" className="py-20 lg:py-28 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
              Ready to prepare for what comes next?
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-2xl mx-auto">
              A focused conversation can help clarify priorities, risks, and execution readiness.
            </p>
            <div className="flex items-center justify-center space-x-6">
              <a
                href="mailto:consult@nexstephr.com"
                className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-400 hover:bg-amber-500 hover:text-slate-900 transition-all duration-200"
                title="Email Us"
              >
                <Mail size={24} />
              </a>
              <a
                href="https://wa.me/918928440225"
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-400 hover:bg-amber-500 hover:text-slate-900 transition-all duration-200"
                title="WhatsApp Us"
              >
                <MessageCircle size={24} />
              </a>
              <a
                href="tel:+918928440225"
                className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-400 hover:bg-amber-500 hover:text-slate-900 transition-all duration-200"
                title="Call Us"
              >
                <Phone size={24} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
